import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  Bell,
  Box,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Headphones,
  Home,
  LogOut,
  Menu,
  MessageSquareText,
  Package,
  Paperclip,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Settings,
  Truck,
  Users,
  Wrench,
  X,
} from "lucide-react";
import type { SessionPayload } from "./AuthenticatedApp";
import { MaintenanceWorkOrders } from "./MaintenanceWorkOrders";
import { PpmWorkspace } from "./PpmWorkspace";
import { HelpdeskWorkspace } from "./HelpdeskWorkspace";
import {
  AssetWorkspace,
  InventoryWorkspace,
  NotificationWorkspace,
  ReportsWorkspace,
} from "./CmmsAdminWorkspaces";
import { AttachmentWorkspace } from "./AttachmentWorkspace";
import { UserAdminWorkspace } from "./UserAdminWorkspace";
import { JobTypeSettings } from "./JobTypeSettings";
import { FeedbackWorkspace } from "./FeedbackWorkspace";
import { BillingWorkspace } from "./BillingWorkspace";
import { QrScanWorkspace } from "./QrScanWorkspace";
import { AppointmentsWorkspace } from "./AppointmentsWorkspace";

const apiOrigin = location.hostname.endsWith(".taskroute.co.uk")
  ? "https://taskroute.co.uk"
  : "";
const tenant = () => localStorage.getItem("taskroute-tenant") || "northstar";
export const request = (path: string, init: RequestInit = {}) =>
  fetch(`${apiOrigin}${path}`, {
    credentials: "include",
    ...init,
    headers: { "x-tenant-slug": tenant(), ...(init.headers || {}) },
  });
export async function downloadProtected(path: string, filename: string) {
  const response = await request(path);
  if (!response.ok) throw new Error("Unable to generate QR label");
  const href = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
const maintenanceSections = [
  ["Overview", Home],
  ["Work orders", ClipboardList],
  ["PPM", CalendarDays],
  ["Assets", Box],
  ["Inventory", Package],
  ["Attachments", Paperclip],
  ["Helpdesk", Headphones],
  ["Notifications", Bell],
  ["Reports", BarChart3],
  ["Feedback", MessageSquareText],
  ["Scan", QrCode],
] as const;
const dispatchSections = [
  ["Overview", Home],
  ["Dispatch orders", Truck],
  ["Manifests", ClipboardList],
  ["Reports", BarChart3],
] as const;
const appointmentSections = [["Appointments", CalendarDays]] as const;
type Dashboard = {
  workOrders: any[];
  parts: any[];
  occurrences: any[];
  tickets: any[];
  orders: any[];
  products?: ("MAINTENANCE" | "DISPATCH" | "APPOINTMENTS")[];
};
type Asset = {
  id: string;
  name: string;
  type: string;
  path: string;
  externalRef?: string;
};

export function FunctionalApp({ session }: { session: SessionPayload }) {
  const initialScan = new URLSearchParams(location.search).get("scan") || "";
  const initialProduct = session.user.products.includes("MAINTENANCE") ? "MAINTENANCE" : session.user.products.includes("DISPATCH") ? "DISPATCH" : "APPOINTMENTS";
  const [product, setProduct] = useState<"MAINTENANCE" | "DISPATCH" | "APPOINTMENTS">(
    initialProduct,
  );
  const [section, setSection] = useState(initialScan ? "Scan" : "Overview");
  const [data, setData] = useState<Dashboard>({
    workOrders: [],
    parts: [],
    occurrences: [],
    tickets: [],
    orders: [],
  });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [mobile, setMobile] = useState(false);
  const [create, setCreate] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [scanTarget, setScanTarget] = useState<{
    type: string;
    id: string;
  } | null>(null);
  const canAdmin =
    session.user.isTenantOwner ||
    session.user.permissions.includes("*") ||
    session.user.permissions.includes("platform.users.manage");
  const sections = [
    ...(product === "MAINTENANCE" ? maintenanceSections : product === "DISPATCH" ? dispatchSections : appointmentSections),
    ...(canAdmin ? [["Admin", Settings] as const] : []),
    ...(session.user.isTenantOwner ? [["Billing", CreditCard] as const] : []),
  ];
  async function load() {
    setLoading(true);
    setError("");
    try {
      const d = await request("/api/dashboard");
      if (!d.ok) throw new Error();
      setData(await d.json());
      if (session.user.products.includes("MAINTENANCE")) {
        const a = await request("/api/assets");
        if (a.ok) setAssets(await a.json());
      }
    } catch {
      setError("Unable to load tenant data.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const filtered = useMemo(
    () =>
      data.workOrders.filter((w) =>
        `${w.number} ${w.title} ${w.status}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [data.workOrders, query],
  );
  async function logout() {
    await request("/api/auth/logout", { method: "POST" });
    location.reload();
  }
  async function resetAccounts() {
    if (
      !confirm(
        "Reset all Northstar demo accounts to a fresh shared demo password?",
      )
    )
      return;
    const r = await request("/api/admin/demo-accounts/reset", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password: "TaskRouteDemo!2026" }),
    });
    if (r.ok) setAccounts((await r.json()).accounts);
    else
      setError(
        "Only the seeded Northstar administrator can reset demo accounts.",
      );
  }
  function switchProduct(next: "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS") {
    setProduct(next);
    setSection(next === "APPOINTMENTS" ? "Appointments" : "Overview");
    setMobile(false);
  }
  const initials = session.user.name
    .split(/\s+/)
    .map((x) => x[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="func-app">
      <div
        className={`func-scrim ${mobile ? "show" : ""}`}
        onClick={() => setMobile(false)}
      />
      <aside className={mobile ? "open" : ""}>
        <div className="brand">
          <span className="mark">◆</span>TaskRoute
          <button className="close" onClick={() => setMobile(false)}>
            <X />
          </button>
        </div>
        <div className="func-tenant">
          <small>Company</small>
          <strong>{session.tenant.name}</strong>
        </div>
        <nav>
          {sections.map(([name, Icon]) => (
            <button
              key={name}
              className={section === name ? "active" : ""}
              onClick={() => {
                setSection(name);
                setMobile(false);
              }}
            >
              <Icon />
              {name}
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <button onClick={logout}>
            <LogOut />
            Sign out
          </button>
        </div>
      </aside>
      <main>
        <header className="func-top">
          <button className="menu" onClick={() => setMobile(true)}>
            <Menu />
          </button>
          <div>
            <b>{section}</b>
            <small>{session.tenant.name}</small>
          </div>
          {session.user.products.includes("MAINTENANCE") ? (
            <button
              className={`func-product ${product === "MAINTENANCE" ? "selected" : ""}`}
              onClick={() => switchProduct("MAINTENANCE")}
            >
              <Wrench />
              Maintenance
            </button>
          ) : null}
          {session.user.products.includes("DISPATCH") ? (
            <button
              className={`func-product ${product === "DISPATCH" ? "selected" : ""}`}
              onClick={() => switchProduct("DISPATCH")}
            >
              <Truck />
              Dispatch
            </button>
          ) : null}
          {session.user.products.includes("APPOINTMENTS") ? (
            <button className={`func-product ${product === "APPOINTMENTS" ? "selected" : ""}`} onClick={() => switchProduct("APPOINTMENTS")}><CalendarDays />Appointments</button>
          ) : null}
          <div className="func-user" title={session.user.name}>
            {initials}
          </div>
        </header>
        <div className="func-content">
          {error ? (
            <div className="func-error">
              {error}
              <button onClick={load}>Retry</button>
            </div>
          ) : null}
          {loading ? (
            <div className="func-loading">Loading workspace…</div>
          ) : (
            <Screen
              product={product}
              section={section}
              data={data}
              assets={assets}
              filtered={filtered}
              query={query}
              setQuery={setQuery}
              setCreate={setCreate}
              resetAccounts={resetAccounts}
              accounts={accounts}
              reload={load}
              scanTarget={scanTarget}
              initialScan={initialScan}
              openScanTarget={(type: string, id: string) => {
                setScanTarget({ type, id });
                setSection(
                  type === "PART"
                    ? "Inventory"
                    : type === "ASSET"
                      ? "Assets"
                      : "Work orders",
                );
              }}
            />
          )}
        </div>
      </main>
      {create && product === "MAINTENANCE" ? (
        <CreateWorkOrder
          assets={assets}
          close={() => setCreate(false)}
          saved={() => {
            setCreate(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function Screen(p: any) {
  if (p.product === "APPOINTMENTS" && p.section === "Appointments") return <AppointmentsWorkspace />;
  if (
    p.product === "DISPATCH" &&
    !["Reports", "Admin", "Manifests", "Billing"].includes(p.section)
  )
    return <DispatchWorkspace />;
  if (p.section === "Manifests") return <ManifestWorkspace />;
  if (p.section === "Overview")
    return (
      <>
        <div className="func-heading">
          <div>
            <h1>Maintenance overview</h1>
            <p>Live maintenance workload for this workspace.</p>
          </div>
          <button onClick={() => p.setCreate(true)}>
            <Plus />
            Create work order
          </button>
        </div>
        <div className="func-kpis">
          <Kpi
            label="Open work orders"
            value={
              p.data.workOrders.filter((w: any) => w.status !== "COMPLETED")
                .length
            }
          />
          <Kpi
            label="Production stopped"
            value={
              p.data.workOrders.filter(
                (w: any) => w.priority === "PRODUCTION_STOPPED",
              ).length
            }
          />
          <Kpi label="Assets" value={p.assets.length} />
          <Kpi label="Parts" value={p.data.parts.length} />
        </div>
        <WorkTable rows={p.filtered} query={p.query} setQuery={p.setQuery} />
      </>
    );
  if (p.section === "Work orders")
    return (
      <MaintenanceWorkOrders
        initialId={
          p.scanTarget?.type === "WORK_ORDER" ? p.scanTarget.id : undefined
        }
      />
    );
  if (p.section === "PPM") return <PpmWorkspace />;
  if (p.section === "Assets")
    return (
      <AssetWorkspace
        initialId={p.scanTarget?.type === "ASSET" ? p.scanTarget.id : undefined}
      />
    );
  if (p.section === "Inventory")
    return (
      <InventoryWorkspace
        initialId={p.scanTarget?.type === "PART" ? p.scanTarget.id : undefined}
      />
    );
  if (p.section === "Attachments") return <AttachmentWorkspace />;
  if (p.section === "Helpdesk") return <HelpdeskWorkspace />;
  if (p.section === "Dispatch orders")
    return <DispatchOrders orders={p.data.orders} />;
  if (p.section === "Notifications") return <NotificationWorkspace />;
  if (p.section === "Feedback") return <FeedbackWorkspace />;
  if (p.section === "Scan")
    return (
      <QrScanWorkspace onOpen={p.openScanTarget} initialCode={p.initialScan} />
    );
  if (p.section === "Reports")
    return p.product === "MAINTENANCE" ? (
      <ReportsWorkspace />
    ) : (
      <>
        <div className="func-heading">
          <div>
            <h1>Reports</h1>
            <p>Dispatch reports and complete tenant export.</p>
          </div>
          <a className="func-button" href={`${apiOrigin}/api/exports/complete`}>
            <BarChart3 />
            Complete export
          </a>
        </div>
      </>
    );
  if (p.section === "Admin")
    return (
      <>
        <BrandingSettings />
        {p.product === "MAINTENANCE" ? <JobTypeSettings /> : null}
        <UserAdminWorkspace />
      </>
    );
  if (p.section === "Billing") return <BillingWorkspace />;
  return null;
}

function ManifestWorkspace() {
  const [loads, setLoads] = useState<any[]>([]),
    [loadId, setLoadId] = useState(""),
    [detail, setDetail] = useState<any>(null),
    [history, setHistory] = useState<any[]>([]),
    [message, setMessage] = useState("");
  async function refresh(preferred = loadId) {
    const [l, m] = await Promise.all([
      request("/api/dispatch/load-state"),
      request("/api/dispatch/manifests"),
    ]);
    const loadRows = l.ok ? (await l.json()).loads : [],
      manifestRows = m.ok ? (await m.json()).manifests : [];
    setLoads(loadRows);
    setHistory(manifestRows);
    const selected = preferred || loadRows[0]?.id || "";
    setLoadId(selected);
    if (selected) {
      const d = await request(`/api/dispatch/loads/${selected}/manifest`);
      if (d.ok) setDetail(await d.json());
    }
  }
  useEffect(() => {
    refresh();
  }, []);
  async function action(path: string, body: any = {}) {
    const r = await request(path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
      x = await r.json();
    setMessage(x.message || x.error || "Saved");
    if (r.ok) await refresh(loadId);
  }
  async function remove(p: any) {
    const reason = prompt(`Reason for removing ${p.sscc} from this trailer`);
    if (reason)
      await action(
        `/api/dispatch/loads/${loadId}/pallets/${p.pallet_id}/remove`,
        { reason },
      );
  }
  async function printManifest(id: string) {
    const r = await request(`/api/dispatch/manifests/${id}/print`);
    if (!r.ok) return setMessage("Unable to generate manifest");
    const url = URL.createObjectURL(
      new Blob([await r.text()], { type: "text/html" }),
    );
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  const current = detail?.current;
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Trailer manifests</h1>
          <p>
            Review, approve, sign and close one uniquely identified trailer.
          </p>
        </div>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <section className="func-panel manifest-selector">
        <label>
          Active trailer
          <select
            value={loadId}
            onChange={(e) => {
              setLoadId(e.target.value);
              refresh(e.target.value);
            }}
          >
            <option value="">Select trailer</option>
            {loads.map((x) => (
              <option key={x.id} value={x.id}>
                {x.destination_code} · {x.trailer_number} · {x.status}
              </option>
            ))}
          </select>
        </label>
      </section>
      {detail ? (
        <>
          <div className="manifest-summary">
            <Kpi label="Pallets" value={detail.totals.pallets} />
            <Kpi label="Orders" value={detail.totals.orders} />
            <Kpi label="Boxes" value={detail.totals.boxes} />
            <Kpi label="Weight kg" value={detail.totals.weightKg} />
          </div>
          <section className="func-panel manifest-actions">
            <div>
              <strong>
                {detail.load.destination_code} · Trailer{" "}
                {detail.load.trailer_number}
              </strong>
              <span>Load status: {detail.load.status}</span>
              <span>Manifest: {current?.status || "Not prepared"}</span>
            </div>
            {detail.load.status === "LOADING" ? (
              <button
                onClick={() => action(`/api/dispatch/loads/${loadId}/full`)}
              >
                Mark trailer Full
              </button>
            ) : null}
            {detail.load.status === "FULL" && !current ? (
              <button
                onClick={() =>
                  action(`/api/dispatch/loads/${loadId}/manifest/draft`)
                }
              >
                Prepare draft manifest
              </button>
            ) : null}
            {current?.status === "DRAFT" ? (
              <button
                onClick={() =>
                  action(`/api/dispatch/manifests/${current.id}/approve`)
                }
              >
                Approve manifest
              </button>
            ) : null}
            {current?.status === "APPROVED" ? (
              <DriverSignature
                manifestId={current.id}
                completed={() => refresh(loadId)}
                setMessage={setMessage}
              />
            ) : null}
            {current?.status === "SIGNED" ? (
              <button
                className="danger"
                onClick={() =>
                  confirm(
                    "Close this trailer and dispatch every assigned pallet?",
                  ) && action(`/api/dispatch/manifests/${current.id}/close`)
                }
              >
                Close trailer
              </button>
            ) : null}
            {current ? (
              <button onClick={() => printManifest(current.id)}>
                Print manifest
              </button>
            ) : null}
          </section>
          <section className="func-panel">
            <div className="func-table-head">
              <h2>Manifest contents</h2>
            </div>
            <div className="manifest-pallets">
              {detail.pallets.map((p: any) => (
                <div key={p.pallet_id}>
                  <span>
                    <strong>{p.sscc}</strong>
                    <small>
                      {p.order_number} · {p.customer}
                    </small>
                  </span>
                  <span>{p.boxes} boxes</span>
                  <span>{p.total_weight_kg} kg</span>
                  {!["CLOSED"].includes(current?.status) &&
                  detail.load.status !== "CLOSED" ? (
                    <button onClick={() => remove(p)}>Remove</button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
      <section className="func-panel manifest-history">
        <div className="func-table-head">
          <h2>Manifest history</h2>
        </div>
        {history.map((m) => (
          <div key={m.id}>
            <span>
              <strong>{m.reference}</strong>
              <small>
                {m.destination_code} · {m.trailer_number}
              </small>
            </span>
            <em>{m.status}</em>
            <span>{m.driver_name || "Unsigned"}</span>
            <button onClick={() => printManifest(m.id)}>Print</button>
            {m.status === "CLOSED" ? (
              <button
                onClick={() => {
                  const reason = prompt(
                    "Mandatory reason for reopening this closed trailer",
                  );
                  if (reason)
                    action(`/api/dispatch/manifests/${m.id}/reopen`, {
                      reason,
                    });
                }}
              >
                Reopen
              </button>
            ) : null}
          </div>
        ))}
      </section>
    </>
  );
}
function DriverSignature({ manifestId, completed, setMessage }: any) {
  const canvas = useRef<HTMLCanvasElement>(null),
    drawing = useRef(false),
    [driverName, setDriverName] = useState(""),
    [registration, setRegistration] = useState("");
  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvas.current!,
      r = c.getBoundingClientRect();
    return [
      (e.clientX - r.left) * (c.width / r.width),
      (e.clientY - r.top) * (c.height / r.height),
    ];
  }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    const [x, y] = point(e),
      ctx = canvas.current!.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const [x, y] = point(e),
      ctx = canvas.current!.getContext("2d")!;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#102f42";
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function clear() {
    canvas
      .current!.getContext("2d")!
      .clearRect(0, 0, canvas.current!.width, canvas.current!.height);
  }
  async function sign(e: FormEvent) {
    e.preventDefault();
    const r = await request(`/api/dispatch/manifests/${manifestId}/sign`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          driverName,
          vehicleRegistration: registration,
          signatureData: canvas.current!.toDataURL("image/png"),
        }),
      }),
      x = await r.json();
    setMessage(r.ok ? "Driver signature saved" : x.error);
    if (r.ok) completed();
  }
  return (
    <form className="signature-form" onSubmit={sign}>
      <input
        placeholder="Driver name"
        value={driverName}
        onChange={(e) => setDriverName(e.target.value)}
        required
      />
      <input
        placeholder="Vehicle registration"
        value={registration}
        onChange={(e) => setRegistration(e.target.value.toUpperCase())}
        required
      />
      <canvas
        ref={canvas}
        width="700"
        height="180"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={() => (drawing.current = false)}
        onPointerCancel={() => (drawing.current = false)}
      />
      <button type="button" onClick={clear}>
        Clear
      </button>
      <button>Save signature</button>
    </form>
  );
}
function DispatchWorkspace() {
  const [t, setT] = useState("Overview"),
    [data, setData] = useState<any>({
      orders: [],
      pallets: [],
      jobs: [],
      loads: [],
    }),
    [message, setMessage] = useState("");
  async function load() {
    const [r, l] = await Promise.all([
      request("/api/dispatch/bootstrap"),
      request("/api/dispatch/load-state"),
    ]);
    if (r.ok) {
      const next = await r.json();
      if (l.ok) next.loads = (await l.json()).loads;
      setData(next);
    } else setMessage("Your account does not have Dispatch access.");
  }
  useEffect(() => {
    load();
  }, []);
  const tabs = [
    "Overview",
    "Order input",
    "Pick queue",
    "Runner queue",
    "Loads",
    "Order search",
  ];
  return (
    <>
      <div className="dispatch-tabs">
        {tabs.map((x) => (
          <button
            className={t === x ? "selected" : ""}
            onClick={() => setT(x)}
            key={x}
          >
            {x}
          </button>
        ))}
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <DuplicateLoadRecovery
        loads={data.loads}
        saved={load}
        setMessage={setMessage}
      />
      {t === "Overview" ? (
        <>
          <div className="func-heading">
            <div>
              <h1>Dispatch overview</h1>
              <p>
                Live orders, pallets, runner work and active loading positions.
              </p>
            </div>
          </div>
          <div className="func-kpis">
            <Kpi label="Orders" value={data.orders.length} />
            <Kpi label="Pallets" value={data.pallets.length} />
            <Kpi
              label="Waiting for runner"
              value={
                data.jobs.filter((x: any) => x.status === "WAITING").length
              }
            />
            <Kpi label="Active loads" value={data.loads.length} />
          </div>
          <List
            title="Current pallets"
            columns={["Pallet", "Order", "Customer", "Status", "Location"]}
            rows={data.pallets
              .slice(0, 20)
              .map((x: any) => [
                x.sscc,
                x.order_number,
                x.customer,
                x.status,
                x.location || "Not staged",
              ])}
            empty="No pallets found."
          />
        </>
      ) : t === "Order input" ? (
        <DispatchOrderInput saved={load} setMessage={setMessage} />
      ) : t === "Pick queue" ? (
        <DispatchPickQueue
          pallets={data.pallets}
          saved={load}
          setMessage={setMessage}
        />
      ) : t === "Runner queue" ? (
        <DispatchRunner
          jobs={data.jobs}
          loads={data.loads}
          saved={load}
          setMessage={setMessage}
        />
      ) : t === "Loads" ? (
        <DispatchLoads
          loads={data.loads}
          saved={load}
          setMessage={setMessage}
        />
      ) : (
        <DispatchSearch setMessage={setMessage} />
      )}
    </>
  );
}
function DuplicateLoadRecovery({ loads, saved, setMessage }: any) {
  const duplicated = new Set(
    loads
      .filter(
        (x: any) =>
          loads.filter((y: any) => y.destination_code === x.destination_code)
            .length > 1,
      )
      .map((x: any) => x.destination_code),
  );
  if (!duplicated.size) return null;
  async function act(load: any, action: string) {
    if (
      action === "assign" &&
      !confirm(
        `Confirm that trailer ${load.trailer_number} physically contains the unassigned pallets at ${load.destination_code}.`,
      )
    )
      return;
    const r = await request(
        `/api/dispatch/loads/${load.id}/${action === "assign" ? "assign-unassigned" : "close-duplicate"}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}",
        },
      ),
      x = await r.json();
    setMessage(x.message || x.error);
    if (r.ok) saved();
  }
  return (
    <section className="duplicate-recovery">
      <h2>Duplicate loading position requires attention</h2>
      <p>
        Select the trailer that physically contains the existing pallets, then
        close the other empty duplicate.
      </p>
      {loads
        .filter((x: any) => duplicated.has(x.destination_code))
        .map((x: any) => (
          <div key={x.id}>
            <strong>
              {x.destination_code} · Trailer {x.trailer_number}
            </strong>
            <span>
              {x.loaded_count} assigned · {x.unassigned_count} awaiting
              assignment
            </span>
            <button onClick={() => act(x, "assign")}>
              This trailer contains the pallets
            </button>
            <button onClick={() => act(x, "close")}>
              Close empty duplicate
            </button>
          </div>
        ))}
    </section>
  );
}
function DispatchOrderInput({ saved, setMessage }: any) {
  const [f, setF] = useState({
    number: "",
    customer: "",
    routeCode: "AD",
    palletCount: 1,
    boxes: 0,
    totalWeightKg: 0,
    collection: false,
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/orders", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(f),
      }),
      x = await r.json();
    setMessage(
      r.ok ? `Created ${x.number}: ${x.palletIds.join(", ")}` : x.error,
    );
    if (r.ok) {
      setF({ ...f, number: "", customer: "" });
      saved();
    }
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Create dispatch order</h2>
      </div>
      <form className="func-brand-form" onSubmit={submit}>
        <label>
          Order number
          <input
            required
            value={f.number}
            onChange={(e) =>
              setF({ ...f, number: e.target.value.toUpperCase() })
            }
          />
        </label>
        <label>
          Customer
          <input
            required
            value={f.customer}
            onChange={(e) => setF({ ...f, customer: e.target.value })}
          />
        </label>
        <label>
          Route
          <select
            value={f.routeCode}
            onChange={(e) => setF({ ...f, routeCode: e.target.value })}
          >
            <option>AD</option>
            <option>AL</option>
            <option>FEDEX</option>
            <option>EXPORT</option>
            <option>COLLECTION</option>
          </select>
        </label>
        <label>
          Pallets
          <input
            type="number"
            min="1"
            max="999"
            value={f.palletCount}
            onChange={(e) =>
              setF({ ...f, palletCount: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Boxes
          <input
            type="number"
            min="0"
            value={f.boxes}
            onChange={(e) => setF({ ...f, boxes: Number(e.target.value) })}
          />
        </label>
        <label>
          Total weight kg
          <input
            type="number"
            min="0"
            step="0.001"
            value={f.totalWeightKg}
            onChange={(e) =>
              setF({ ...f, totalWeightKg: Number(e.target.value) })
            }
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={f.collection}
            onChange={(e) => setF({ ...f, collection: e.target.checked })}
          />{" "}
          Customer collection
        </label>
        <button>Create order and pallets</button>
      </form>
    </section>
  );
}
function DispatchPickQueue({ pallets, saved, setMessage }: any) {
  const [sscc, setSscc] = useState(""),
    [location, setLocation] = useState("PICKED-61");
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request(
        `/api/dispatch/pallets/${encodeURIComponent(sscc)}/stage`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ location }),
        },
      ),
      x = await r.json();
    setMessage(r.ok ? x.message : x.error);
    if (r.ok) {
      setSscc("");
      saved();
    }
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Stage a picked pallet</h2>
        </div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input
            placeholder="Scan pallet barcode"
            value={sscc}
            onChange={(e) => setSscc(e.target.value.toUpperCase())}
            required
          />
          <input
            placeholder="Staging location"
            value={location}
            onChange={(e) => setLocation(e.target.value.toUpperCase())}
            required
          />
          <button>Stage pallet</button>
        </form>
      </section>
      <List
        title="Awaiting staging"
        columns={["Pallet", "Order", "Customer", "Status"]}
        rows={pallets
          .filter((x: any) => x.status === "CREATED")
          .map((x: any) => [x.sscc, x.order_number, x.customer, x.status])}
        empty="No pallets are awaiting staging."
      />
    </>
  );
}
function DispatchRunner({ jobs, loads, saved, setMessage }: any) {
  const [barcode, setBarcode] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/runner/scan", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ barcode }),
      }),
      x = await r.json();
    setMessage(r.ok ? x.message : x.error);
    if (r.ok) {
      setBarcode("");
      saved();
    }
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Two-scan runner workflow</h2>
        </div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input
            autoFocus
            placeholder="Scan pallet, trailer position or PRE-LOAD"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value.toUpperCase())}
          />
          <button>Process scan</button>
        </form>
      </section>
      <List
        title="Shared runner queue"
        columns={["Pallet", "Order", "From", "Destination", "Status"]}
        rows={jobs.map((x: any) => [
          x.sscc,
          x.order_number,
          x.staging_location,
          x.destination,
          x.status,
        ])}
        empty="Runner queue is clear."
      />
      <List
        title="Loading positions"
        columns={[
          "Position",
          "Trailer",
          "Route",
          "Loaded",
          "Capacity",
          "Status",
        ]}
        rows={loads.map((x: any) => [
          x.destination_code,
          x.trailer_number,
          x.route_code,
          x.loaded_count,
          x.capacity,
          x.status,
        ])}
        empty="No loading positions are open."
      />
    </>
  );
}
function DispatchLoads({ loads, saved, setMessage }: any) {
  const [f, setF] = useState({
    destinationCode: "AD-1",
    routeCode: "AD",
    trailerNumber: "",
    capacity: 24,
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request("/api/dispatch/loads", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(f),
      }),
      x = await r.json();
    setMessage(
      r.ok ? `${f.trailerNumber} assigned to ${f.destinationCode}` : x.error,
    );
    if (r.ok) {
      setF({ ...f, trailerNumber: "" });
      saved();
    }
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Assign trailer or container</h2>
        </div>
        <form className="func-brand-form" onSubmit={submit}>
          <label>
            Loading position
            <input
              value={f.destinationCode}
              onChange={(e) =>
                setF({ ...f, destinationCode: e.target.value.toUpperCase() })
              }
            />
          </label>
          <label>
            Route
            <input
              value={f.routeCode}
              onChange={(e) =>
                setF({ ...f, routeCode: e.target.value.toUpperCase() })
              }
            />
          </label>
          <label>
            Trailer/container number
            <input
              required
              value={f.trailerNumber}
              onChange={(e) =>
                setF({ ...f, trailerNumber: e.target.value.toUpperCase() })
              }
            />
          </label>
          <label>
            Capacity
            <input
              type="number"
              min="1"
              value={f.capacity}
              onChange={(e) => setF({ ...f, capacity: Number(e.target.value) })}
            />
          </label>
          <button>Open loading position</button>
        </form>
      </section>
      <List
        title="Open loads"
        columns={[
          "Position",
          "Trailer",
          "Route",
          "Loaded",
          "Capacity",
          "Status",
        ]}
        rows={loads.map((x: any) => [
          x.destination_code,
          x.trailer_number,
          x.route_code,
          x.loaded_count,
          x.capacity,
          x.status,
        ])}
        empty="No loads open."
      />
    </>
  );
}
function DispatchSearch({ setMessage }: any) {
  const [term, setTerm] = useState(""),
    [result, setResult] = useState<any>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await request(`/api/dispatch/status/${encodeURIComponent(term)}`),
      x = await r.json();
    if (r.ok) setResult(x);
    else {
      setResult(null);
      setMessage(x.error);
    }
  }
  return (
    <>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Order and pallet search</h2>
        </div>
        <form className="dispatch-scan" onSubmit={submit}>
          <input
            placeholder="Order number or pallet barcode"
            value={term}
            onChange={(e) => setTerm(e.target.value.toUpperCase())}
          />
          <button>Search</button>
        </form>
      </section>
      {result ? (
        <>
          <List
            title={`${result.order.number} — ${result.order.customer}`}
            columns={["Pallet", "Status", "Location"]}
            rows={result.pallets.map((x: any) => [
              x.sscc,
              x.status,
              x.location || "—",
            ])}
            empty="No pallets."
          />
          <List
            title="Movement history"
            columns={["Time", "Pallet", "From", "To", "Result", "Actor"]}
            rows={result.history.map((x: any) => [
              new Date(x.created_at).toLocaleString(),
              x.pallet_id,
              x.from_location || "—",
              x.to_location,
              x.result,
              x.actor,
            ])}
            empty="No movements recorded."
          />
        </>
      ) : null}
    </>
  );
}
function BrandingSettings() {
  const [form, setForm] = useState({
      logoUrl: "",
      primaryColor: "#102a43",
      accentColor: "#12a594",
      signInHeading: "",
      welcomeMessage: "",
    }),
    [message, setMessage] = useState("");
  useEffect(() => {
    request("/api/branding").then(async (r) => {
      if (r.ok) {
        const b = await r.json();
        setForm({
          logoUrl: b.logoUrl || "",
          primaryColor: b.primaryColor,
          accentColor: b.accentColor,
          signInHeading: b.signInHeading,
          welcomeMessage: b.welcomeMessage,
        });
      }
    });
  }, []);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMessage("Saving…");
    const r = await request("/api/branding", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(form),
    });
    setMessage(
      r.ok
        ? "Branding saved. The login page will update immediately."
        : "Only the tenant owner can update branding.",
    );
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Client login branding</h2>
      </div>
      <form className="func-brand-form" onSubmit={save}>
        <label>
          Logo URL (HTTPS)
          <input
            type="url"
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://example.com/logo.png"
          />
        </label>
        <label>
          Sign-in heading
          <input
            value={form.signInHeading}
            onChange={(e) =>
              setForm({ ...form, signInHeading: e.target.value })
            }
            maxLength={120}
          />
        </label>
        <label>
          Welcome text
          <input
            value={form.welcomeMessage}
            onChange={(e) =>
              setForm({ ...form, welcomeMessage: e.target.value })
            }
            maxLength={300}
          />
        </label>
        <label>
          Primary colour
          <input
            type="color"
            value={form.primaryColor}
            onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
          />
        </label>
        <label>
          Accent colour
          <input
            type="color"
            value={form.accentColor}
            onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
          />
        </label>
        <button>Save login branding</button>
        {message ? <span>{message}</span> : null}
      </form>
    </section>
  );
}
function DispatchOrders({ orders }: { orders: any[] }) {
  return (
    <List
      title="Dispatch orders"
      columns={["Order", "Customer", "Status", "Collection"]}
      rows={orders.map((x: any) => [
        x.number,
        x.customer,
        x.status,
        x.collection ? "Yes" : "No",
      ])}
      empty="No dispatch orders found."
    />
  );
}
function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="func-kpi">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function WorkTable({ rows, query, setQuery }: any) {
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Work orders</h2>
        <label>
          <Search />
          <input
            placeholder="Search work orders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="func-table">
        <div className="func-row func-th">
          <span>Number</span>
          <span>Issue</span>
          <span>Priority</span>
          <span>Status</span>
          <span>Reported</span>
        </div>
        {rows.map((w: any) => (
          <div className="func-row" key={w.id}>
            <b>{w.number}</b>
            <span>{w.title}</span>
            <span>{w.priority.replaceAll("_", " ")}</span>
            <span>
              <em>{w.status.replaceAll("_", " ")}</em>
            </span>
            <span>{new Date(w.reportedAt).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
function List({
  title,
  columns,
  rows,
  empty,
}: {
  title: string;
  columns: string[];
  rows: any[][];
  empty: string;
}) {
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>{title}</h2>
      </div>
      <div className="func-list" style={{ "--cols": columns.length } as any}>
        <div className="func-list-row func-th">
          {columns.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        {rows.length ? (
          rows.map((r, i) => (
            <div className="func-list-row" key={i}>
              {r.map((v, j) => (
                <span key={j}>{String(v)}</span>
              ))}
            </div>
          ))
        ) : (
          <p className="func-empty">{empty}</p>
        )}
      </div>
    </section>
  );
}
function CreateWorkOrder({ assets, close, saved }: any) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assetId, setAssetId] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await request("/api/work-orders", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title,
        description,
        priority,
        assetId: assetId || null,
      }),
    });
    setBusy(false);
    if (r.ok) saved();
  }
  return (
    <div className="func-modal">
      <form onSubmit={submit}>
        <header>
          <h2>Create work order</h2>
          <button type="button" onClick={close}>
            <X />
          </button>
        </header>
        <label>
          Issue title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
        <label>
          Asset
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">No asset selected</option>
            {assets.map((a: Asset) => (
              <option value={a.id} key={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="PRODUCTION_CAN_RUN">Production can run</option>
            <option value="PRODUCTION_STOPPED">Production stopped</option>
          </select>
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <footer>
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button disabled={busy}>
            {busy ? "Saving…" : "Create work order"}
          </button>
        </footer>
      </form>
    </div>
  );
}
